import type {
  AttendeeMeetingResponseStatus,
  CalendarEvent,
  CalendarEventInlineBrief,
  GoogleMeetMeeting,
} from '@highlight/common'
import { isNonMeetingEventType, isUserOnEvent } from '@highlight/common/calendars'
import { getChatApiRoute } from '../ChatBackendUtils'
import { isNetworkOffline } from '../NetworkUtils'
import { getConnections } from '.'

// Backend types matching the OneCal API
interface BackendCalendar {
  id: string
  name: string
  readOnly: boolean
  hexColor: string
  isPrimary: boolean
  timeZone: string
  providerName: 'google' | 'microsoft'
  connectionId: string
  metadata?: Record<string, unknown>
}

interface BackendEvent {
  id: string
  title?: string
  description?: string
  etag: string
  createdAt?: string
  updatedAt?: string
  start: {
    dateTime: string
    timeZone?: string
  }
  end: {
    dateTime: string
    timeZone?: string
  }
  isAllDay: boolean
  isRecurring: boolean
  isException: boolean
  isCancelled: boolean
  /** Google's `eventType`; omitted by Microsoft accounts and older backends. */
  eventType?: string
  recurrence: Array<string> | null
  recurringEventId?: string
  myResponseStatus?: 'needsAction' | 'declined' | 'tentative' | 'accepted'
  attendees?: Array<{
    name?: string
    email: string
    responseStatus: 'needsAction' | 'declined' | 'tentative' | 'accepted'
  }>
  visibility?: 'default' | 'public' | 'private' | 'confidential'
  location?: string
  conferenceData?: any
  webLink: string
  transparency?: 'opaque' | 'transparent'
  organizer?: {
    name?: string
    email: string
  }
  /**
   * Latest ready brief for this event (when the backend supports it).
   * Backends that haven't been updated yet omit the field — the mapper
   * defaults to null in that case. Generation lifecycle stays on the
   * per-event GET endpoint.
   */
  brief?: CalendarEventInlineBrief | null
}

/**
 * Fetch all calendars for the authenticated user
 */
export async function fetchCalendars(token: string): Promise<BackendCalendar[]> {
  try {
    // Check if network is offline before making request
    if (isNetworkOffline()) {
      console.warn('Failed to fetch calendars: Network is offline')
      throw new Error('Failed to fetch calendars: Network is offline')
    }

    const response = await fetch(getChatApiRoute('api/v1/calendars'), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      console.error('Failed to fetch calendars', response.status, await response.text())
      throw new Error('Failed to fetch calendars')
    }

    const data = await response.json()
    return (data.data ?? []) as BackendCalendar[]
  } catch (e) {
    console.error('Failed to fetch calendars', e)
    throw e
  }
}

/**
 * Fetch events for a specific calendar
 */
export async function fetchCalendarEvents(calendarId: string, token: string): Promise<BackendEvent[]> {
  try {
    // Check if network is offline before making request
    if (isNetworkOffline()) {
      console.warn(`Failed to fetch events for calendar ${calendarId}: Network is offline`)
      return []
    }

    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()

    const response = await fetch(
      getChatApiRoute(`api/v1/calendars/${calendarId}/events?startDateTime=${encodeURIComponent(sevenDaysAgo)}`),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )

    if (!response.ok) {
      console.error(`Failed to fetch events for calendar ${calendarId}`, response.status, await response.text())
      return []
    }

    const data = await response.json()
    return (data.data ?? []) as BackendEvent[]
  } catch (e) {
    console.error(`Error fetching events for calendar ${calendarId}:`, e)
    return []
  }
}

/**
 * Detects the meeting platform from a URL and returns platform info
 */
function detectMeetingPlatform(url: string): { type: string; name: string } | null {
  let hostname = ''
  try {
    hostname = new URL(url).hostname.toLowerCase()
  } catch {
    // If URL is invalid, return generic conference type
    return { type: 'videoConference', name: 'Video Conference' }
  }

  // Helper to check exact match or subdomain match
  const isHostMatch = (host: string, domain: string): boolean => host === domain || host.endsWith(`.${domain}`)

  if (hostname === 'meet.google.com' || hostname === 'hangouts.google.com') {
    return { type: 'hangoutsMeet', name: 'Google Meet' }
  }

  if (hostname === 'teams.microsoft.com' || hostname === 'teams.live.com') {
    return { type: 'teamsForBusiness', name: 'Microsoft Teams' }
  }

  if (isHostMatch(hostname, 'zoom.us') || isHostMatch(hostname, 'zoom.com')) {
    return { type: 'zoomMeeting', name: 'Zoom Meeting' }
  }

  if (isHostMatch(hostname, 'webex.com')) {
    return { type: 'webexMeeting', name: 'Webex' }
  }

  // Generic video conference
  return { type: 'videoConference', name: 'Video Conference' }
}

/**
 * Extracts meeting URL from location field if it contains a URL
 */
function extractUrlFromLocation(location?: string): string | undefined {
  if (!location) return undefined

  // Check if location contains a URL
  const urlRegex = /(https?:\/\/[^\s]+)/gi
  const matches = location.match(urlRegex)

  if (matches && matches.length > 0) {
    // Return the first URL found
    return matches[0]
  }

  return undefined
}

/** Who is looking at the event and which calendar copy it came from. */
export interface CalendarEventContext {
  calendar: Pick<BackendCalendar, 'id' | 'name' | 'isPrimary'>
  /** Email of the connection that owns `calendar`. */
  userEmail: string
  /** Lowercased emails of all the user's connections. */
  userEmails: ReadonlySet<string>
}

/**
 * The provider's `myResponseStatus` is the RSVP of whoever owns the calendar the
 * copy came from, so on a colleague's calendar it's their answer, not the user's.
 * Off the user's own calendar, read the user's own attendee entry instead.
 */
function getUserResponseStatus(
  backendEvent: BackendEvent,
  isOwnCalendar: boolean,
  userEmails: ReadonlySet<string>,
): AttendeeMeetingResponseStatus | undefined {
  const status = isOwnCalendar
    ? backendEvent.myResponseStatus
    : backendEvent.attendees?.find((attendee) => userEmails.has(attendee.email.toLowerCase()))?.responseStatus
  return status as AttendeeMeetingResponseStatus | undefined
}

/**
 * Map backend event format to CalendarEvent format
 */
export function mapBackendEventToCalendarEvent(
  backendEvent: BackendEvent,
  { calendar, userEmail, userEmails }: CalendarEventContext,
): CalendarEvent {
  // Extract conference data with enhanced Teams/Zoom support
  // OneCal API wraps conference data in a different structure
  let conferenceType =
    backendEvent.conferenceData?.conferenceDetails?.conferenceSolution?.key?.type ||
    backendEvent.conferenceData?.conferenceSolution?.key?.type
  let conferenceName =
    backendEvent.conferenceData?.conferenceDetails?.conferenceSolution?.name ||
    backendEvent.conferenceData?.conferenceSolution?.name ||
    backendEvent.conferenceData?.conferenceType
  let conferenceIconUri =
    backendEvent.conferenceData?.conferenceDetails?.conferenceSolution?.iconUri ||
    backendEvent.conferenceData?.conferenceSolution?.iconUri

  // Try multiple paths for the meeting URI (OneCal API has different structure than raw Google Calendar)
  let conferenceUri =
    backendEvent.conferenceData?.joinUrl || // OneCal top-level joinUrl
    backendEvent.conferenceData?.conferenceDetails?.entryPoints?.find((entry: any) => entry.entryPointType === 'video')
      ?.uri || // OneCal nested entryPoints
    backendEvent.conferenceData?.entryPoints?.find((entry: any) => entry.entryPointType === 'video')?.uri // Direct entryPoints

  // If no conference URI found from conferenceData, try alternative sources
  if (!conferenceUri) {
    // Check if conferenceData is a string URL (some providers send it this way)
    if (typeof backendEvent.conferenceData === 'string' && backendEvent.conferenceData.startsWith('http')) {
      conferenceUri = backendEvent.conferenceData
      const platform = detectMeetingPlatform(conferenceUri)
      if (platform) {
        conferenceType = platform.type || conferenceType
        conferenceName = platform.name || conferenceName
      }
    }
    // Try extracting from location field (common for Teams/Zoom)
    else if (backendEvent.location) {
      const urlFromLocation = extractUrlFromLocation(backendEvent.location)
      if (urlFromLocation) {
        conferenceUri = urlFromLocation
        const platform = detectMeetingPlatform(urlFromLocation)
        if (platform) {
          conferenceType = platform.type || conferenceType
          conferenceName = platform.name || conferenceName
        }
      }
    }
  } else {
    // Even if we have a conferenceUri, check if we can detect platform from it or location
    // This ensures we override OneCal's potentially incorrect conference name
    const platform = detectMeetingPlatform(conferenceUri)
    if (platform) {
      conferenceType = platform.type || conferenceType
      conferenceName = platform.name || conferenceName
    } else if (backendEvent.location) {
      const urlFromLocation = extractUrlFromLocation(backendEvent.location)
      if (urlFromLocation) {
        const platformFromLocation = detectMeetingPlatform(urlFromLocation)
        if (platformFromLocation) {
          conferenceType = platformFromLocation.type || conferenceType
          conferenceName = platformFromLocation.name || conferenceName
        }
      }
    }
  }

  const finalConferenceData = {
    type: conferenceType || '',
    name: conferenceName || '',
    iconUri: conferenceIconUri || '',
    uri: conferenceUri,
  }

  // Parse dates and validate them
  const startDate = new Date(backendEvent.start.dateTime)
  const endDate = new Date(backendEvent.end.dateTime)

  // Log warning for invalid or suspicious dates
  if (isNaN(startDate.getTime())) {
    console.warn(`Invalid start date for event ${backendEvent.id}:`, backendEvent.start.dateTime)
  }
  if (isNaN(endDate.getTime())) {
    console.warn(`Invalid end date for event ${backendEvent.id}:`, backendEvent.end.dateTime)
  }

  return {
    id: backendEvent.id,
    title: backendEvent.title || `Busy (${calendar.name})`,
    start: startDate,
    end: endDate,
    isAllDay: backendEvent.isAllDay,
    isCanceled: backendEvent.isCancelled,
    eventType: backendEvent.eventType,
    isUserOnEvent: isUserOnEvent(backendEvent, calendar.isPrimary, userEmails),
    selfResponseStatus: getUserResponseStatus(backendEvent, calendar.isPrimary, userEmails),
    userEmail,
    organizerEmail: backendEvent.organizer?.email ?? userEmail,
    calendarEventLink: backendEvent.webLink,
    calendarId: calendar.id,
    attendees:
      backendEvent.attendees?.map((attendee) => ({
        email: attendee.email,
        responseStatus: attendee.responseStatus as AttendeeMeetingResponseStatus,
      })) ?? [],
    conferenceData: finalConferenceData,
    brief: backendEvent.brief ?? null,
  }
}

/**
 * Calendar event utility functions
 * These work with the generic CalendarEvent type and are platform-agnostic
 */

export const getMeetingJoinLink = (event: CalendarEvent) => {
  return event.conferenceData?.uri
}

export const getHasEventEnded = (event: CalendarEvent) => !!event.end && new Date(event.end).getTime() < Date.now()

export const getIsGoogleMeet = (event: CalendarEvent) => {
  return event.conferenceData?.name === 'Google Meet'
}

export const getMeetingIcon = (event: CalendarEvent) => {
  return event.conferenceData?.iconUri
}

export const getIsEventDeclined = (event: CalendarEvent) => {
  return event.selfResponseStatus === 'declined'
}

export const getIsEventCanceled = (event: CalendarEvent) => {
  return event.isCanceled === true
}

/**
 * Whether the event is a meeting the user is actually going to: not declined,
 * not cancelled, and not a colleague's meeting synced from a subscribed
 * calendar. Gates join prompts, recording options and calendar matching.
 */
export const getIsUserAttending = (event: CalendarEvent) => {
  return !getIsEventDeclined(event) && !getIsEventCanceled(event) && event.isUserOnEvent !== false
}

/**
 * Calendar entries that hold a slot without being a meeting: Google's
 * working-location, out-of-office, focus-time, birthday and Gmail-reservation
 * event types. They belong on the calendar, but must not drive meeting
 * notifications or brief generation. A working-location entry is all-day, so it
 * starts at local midnight and would otherwise draw a brief at 11:40pm and a
 * "starts in ~10 minutes" toast at 11:50pm.
 *
 * An unknown or absent `eventType` (Microsoft accounts, older backends) counts
 * as a meeting, because missing a suppression is cheaper than dropping a real
 * brief.
 */
export const getIsNonMeetingEvent = (event: CalendarEvent) => {
  return isNonMeetingEventType(event.eventType)
}

/**
 * Build a scheduled-meeting object from a calendar event so a recording can be
 * tied to it even when the event has no conference link. The `id` is the
 * calendar event id, which `getMeetingCalendarEventId` reads back — so the
 * meeting cell recognizes the active recording as this event's and shows
 * "View Transcript" instead of a start CTA. Mirrors the conversion the
 * meeting-start toast performs for a detected meeting matched to a calendar
 * event.
 */
export const calendarEventToScheduledMeeting = (event: CalendarEvent): GoogleMeetMeeting => ({
  id: event.id,
  title: event.title,
  start: event.start,
  end: event.end,
  hangoutLink: getMeetingJoinLink(event),
  attendees: event.attendees.map((attendee) => attendee.email),
  userEmail: event.userEmail,
})

export const getMeetingAppName = (event: CalendarEvent): 'Google Meet' | 'Zoom' | 'Microsoft Teams' | 'Other' => {
  const name = event.conferenceData?.name
  if (name === 'Google Meet') return 'Google Meet'
  if (name === 'Zoom Meeting') return 'Zoom'
  if (name === 'Microsoft Teams') return 'Microsoft Teams'
  return 'Other'
}

/**
 * Fetch all calendar events from enabled calendars
 * @param token - Auth token
 * @param prefetchedConnections - Optional pre-fetched connections to avoid duplicate API calls
 */
export async function fetchAllCalendarEvents(
  token: string,
  prefetchedConnections?: Array<any>,
): Promise<CalendarEvent[]> {
  try {
    // Fetch all calendars
    const calendars = await fetchCalendars(token)

    // Filter to only enabled calendars
    const enabledCalendars = calendars.filter((cal) => cal.metadata?.enabled === true)

    if (enabledCalendars.length === 0) {
      return []
    }

    // Use pre-fetched connections or fetch them
    const connections: Array<{ id: string; email?: string }> = prefetchedConnections ?? (await getConnections(token))
    const connectionEmailMap = new Map(connections.map((conn) => [conn.id, conn.email ?? '']))
    const userEmails = new Set(connections.flatMap((conn) => (conn.email ? [conn.email.toLowerCase()] : [])))

    // Fetch events from all enabled calendars in parallel
    const eventPromises = enabledCalendars.map(async (calendar) => {
      const events = await fetchCalendarEvents(calendar.id, token)
      const userEmail = connectionEmailMap.get(calendar.connectionId) ?? ''

      return events.map((event) => mapBackendEventToCalendarEvent(event, { calendar, userEmail, userEmails }))
    })

    // Wait for all requests to complete
    const eventArrays = await Promise.all(eventPromises)

    // Flatten and deduplicate events by ID. The same event can sync from both
    // the user's and a colleague's calendar; prefer a copy the user is on, then
    // the one on their own calendar, whose brief and calendar id are theirs.
    const allEvents = eventArrays.flat()
    const ownCalendarIds = new Set(enabledCalendars.filter((cal) => cal.isPrimary).map((cal) => cal.id))
    const copyRank = (event: CalendarEvent) =>
      (event.isUserOnEvent ? 2 : 0) + (event.calendarId && ownCalendarIds.has(event.calendarId) ? 1 : 0)
    const uniqueEventsMap = new Map<string, CalendarEvent>()

    allEvents.forEach((event) => {
      const existing = uniqueEventsMap.get(event.id)
      if (!existing || copyRank(event) > copyRank(existing)) {
        uniqueEventsMap.set(event.id, event)
      }
    })

    return Array.from(uniqueEventsMap.values())
  } catch (error) {
    console.error('Failed to fetch all calendar events:', error)
    throw error
  }
}
